import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchBoards, fetchThreads, fetchThread, createPost, updatePost, deletePost } from '../api';

// Community boards panel.
//   - Left: pick a board.
//   - Board view: list of threads (newest first) + a "new post" composer, unless
//     the board is post_admin_only and the viewer isn't an admin (composer hidden;
//     the server enforces this regardless).
//   - Thread view: root post + replies + a reply composer.
//
// Simple in-component navigation (board -> thread) via local state; no router.

function fmt(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString();
}

export default function Boards({ isAdmin }) {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [boards, setBoards] = useState([]);
  const [activeBoard, setActiveBoard] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // { root, replies }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Composer state (shared by new-thread + reply; parentId distinguishes).
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  // Moderation state (admin edit/delete).
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [moderating, setModerating] = useState(false);

  const loadBoards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { ok, data, status } = await fetchBoards(instance, account);
      if (ok) {
        const list = data?.boards || [];
        setBoards(list);
        setActiveBoard((prev) => prev || list[0] || null);
      } else {
        setError((data && (data.message || data.error)) || `HTTP ${status}`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => { loadBoards(); }, [loadBoards]);

  const loadThreads = useCallback(async (boardId) => {
    setActiveThread(null);
    try {
      const { ok, data } = await fetchThreads(instance, account, boardId);
      setThreads(ok ? (data?.threads || []) : []);
    } catch {
      setThreads([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => {
    if (activeBoard) loadThreads(activeBoard.id);
  }, [activeBoard, loadThreads]);

  async function openThread(threadId) {
    setError(null);
    try {
      const { ok, data, status } = await fetchThread(instance, account, threadId);
      if (ok) setActiveThread(data);
      else setError((data && (data.message || data.error)) || `HTTP ${status}`);
    } catch (e) {
      setError(e.message);
    }
  }

  async function submitPost(parentId = null) {
    const text = draft.trim();
    if (!text || !activeBoard) return;
    setPosting(true);
    setError(null);
    try {
      const { ok, data, status } = await createPost(instance, account, activeBoard.id, {
        body: text,
        parentId: parentId || undefined,
      });
      if (!ok) {
        setError((data && (data.message || data.error)) || `HTTP ${status}`);
        return;
      }
      setDraft('');
      if (parentId) {
        await openThread(parentId);      // refresh the open thread
      } else {
        await loadThreads(activeBoard.id); // refresh thread list
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  }

  const canPost = activeBoard && (!activeBoard.post_admin_only || isAdmin);

  function startEdit(post) {
    setEditingId(post.id);
    setEditDraft(post.body || '');
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft('');
  }

  async function saveEdit(post) {
    const text = editDraft.trim();
    if (!text) return;
    setModerating(true);
    setError(null);
    try {
      const { ok, data, status } = await updatePost(instance, account, post.id, { body: text });
      if (!ok) {
        setError((data && (data.message || data.error)) || `HTTP ${status}`);
        return;
      }
      cancelEdit();
      // Refresh: if we're in a thread, reload it; otherwise reload the thread list.
      if (activeThread) await openThread(activeThread.root.id);
      else if (activeBoard) await loadThreads(activeBoard.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setModerating(false);
    }
  }

  async function removePost(post, { isRoot } = {}) {
    const msg = isRoot
      ? 'Delete this thread and all its replies? This cannot be undone.'
      : 'Delete this reply? This cannot be undone.';
    if (!window.confirm(msg)) return;
    setModerating(true);
    setError(null);
    try {
      const { ok, data, status } = await deletePost(instance, account, post.id);
      if (!ok) {
        setError((data && (data.message || data.error)) || `HTTP ${status}`);
        return;
      }
      if (isRoot) {
        // The open thread is gone; go back to the list and refresh.
        setActiveThread(null);
        if (activeBoard) await loadThreads(activeBoard.id);
      } else if (activeThread) {
        await openThread(activeThread.root.id);
      } else if (activeBoard) {
        await loadThreads(activeBoard.id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setModerating(false);
    }
  }

  return (
    <div className="card">
      <h2>Community Boards</h2>
      {error && <p className="error-text">{error}</p>}
      {loading && <p className="muted">Loading boards…</p>}

      {!loading && boards.length > 0 && (
        <div className="board-tabs">
          {boards.map((b) => (
            <button
              key={b.id}
              className={b.id === activeBoard?.id ? 'tab active' : 'tab'}
              onClick={() => { setActiveBoard(b); }}
            >
              {b.name}{b.post_admin_only ? ' 📣' : ''}
            </button>
          ))}
        </div>
      )}

      {activeBoard && !activeThread && (
        <div className="board-view">
          {activeBoard.description && <p className="muted">{activeBoard.description}</p>}

          <ul className="thread-list">
            {threads.map((t) => (
              <li
                key={t.id}
                className="thread-row"
                role="button"
                tabIndex={0}
                onClick={() => openThread(t.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openThread(t.id); }
                }}
              >
                <div className="thread-main">
                  {t.is_announcement ? <span className="badge">Announcement</span> : null}
                  <span className="thread-body">{t.body}</span>
                </div>
                <div className="thread-meta muted">
                  {t.author_name || 'Member'} · {fmt(t.created_at)} · {t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}
                </div>
                {isAdmin && (
                  <div className="mod-actions">
                    <button
                      className="link-btn"
                      disabled={moderating}
                      onClick={(e) => { e.stopPropagation(); removePost(t, { isRoot: true }); }}
                    >
                      Delete thread
                    </button>
                  </div>
                )}
              </li>
            ))}
            {threads.length === 0 && <li className="muted">No posts yet. Be the first.</li>}
          </ul>

          {canPost ? (
            <div className="composer">
              <textarea
                rows={3}
                placeholder={`Post to ${activeBoard.name}…`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button disabled={posting || !draft.trim()} onClick={() => submitPost(null)}>
                {posting ? 'Posting…' : 'Post'}
              </button>
            </div>
          ) : (
            <p className="muted">Only admins can post to this board.</p>
          )}
        </div>
      )}

      {activeThread && (
        <div className="thread-view">
          <button className="link-btn" onClick={() => setActiveThread(null)}>← Back to {activeBoard?.name}</button>

          <div className="post root">
            {activeThread.root.is_announcement ? <span className="badge">Announcement</span> : null}
            {editingId === activeThread.root.id ? (
              <div className="composer">
                <textarea rows={3} value={editDraft} onChange={(e) => setEditDraft(e.target.value)} />
                <div className="mod-actions">
                  <button disabled={moderating || !editDraft.trim()} onClick={() => saveEdit(activeThread.root)}>
                    {moderating ? 'Saving…' : 'Save'}
                  </button>
                  <button className="link-btn" disabled={moderating} onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <p className="post-body">{activeThread.root.body}</p>
                <div className="muted">{activeThread.root.author_name || 'Member'} · {fmt(activeThread.root.created_at)}</div>
                {isAdmin && (
                  <div className="mod-actions">
                    <button className="link-btn" disabled={moderating} onClick={() => startEdit(activeThread.root)}>Edit</button>
                    <button className="link-btn" disabled={moderating} onClick={() => removePost(activeThread.root, { isRoot: true })}>Delete</button>
                  </div>
                )}
              </>
            )}
          </div>

          <ul className="reply-list">
            {activeThread.replies.map((r) => (
              <li key={r.id} className="post reply">
                {editingId === r.id ? (
                  <div className="composer">
                    <textarea rows={2} value={editDraft} onChange={(e) => setEditDraft(e.target.value)} />
                    <div className="mod-actions">
                      <button disabled={moderating || !editDraft.trim()} onClick={() => saveEdit(r)}>
                        {moderating ? 'Saving…' : 'Save'}
                      </button>
                      <button className="link-btn" disabled={moderating} onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="post-body">{r.body}</p>
                    <div className="muted">{r.author_name || 'Member'} · {fmt(r.created_at)}</div>
                    {isAdmin && (
                      <div className="mod-actions">
                        <button className="link-btn" disabled={moderating} onClick={() => startEdit(r)}>Edit</button>
                        <button className="link-btn" disabled={moderating} onClick={() => removePost(r, { isRoot: false })}>Delete</button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
            {activeThread.replies.length === 0 && <li className="muted">No replies yet.</li>}
          </ul>

          {canPost && (
            <div className="composer">
              <textarea
                rows={2}
                placeholder="Write a reply…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button disabled={posting || !draft.trim()} onClick={() => submitPost(activeThread.root.id)}>
                {posting ? 'Posting…' : 'Reply'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
